import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { ChevronDown, Clock, UserX } from 'lucide-react'

import { JobTitleLink } from '@/components/jobs/job-title-link'
import { Badge } from '@/components/ui/badge'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import { withReturnTo } from '@/lib/return-navigation'
import { withServiceInstanceId } from '@/lib/service-instance'
import { formatDateTimeForLocale, useOrganizationDateTimeFormat } from '@/lib/org-date-locale'
import { cn, formatDate } from '@/lib/utils'
import { formatDateOnly, useTenantTimeZone } from '@/lib/tenant-date-time'
import { useWorkspaceLabels } from '@/contexts/workspace-portal-context'
import type { DashboardWorkdayAttendanceAlertItem } from '@/types'

interface WorkDayAttendanceAlertsProps {
  lateCount?: number
  noShowCount?: number
  lateItems?: DashboardWorkdayAttendanceAlertItem[]
  noShowItems?: DashboardWorkdayAttendanceAlertItem[]
  isLoading?: boolean
  serviceInstanceId?: string | null
  returnPath?: string | null
}

export type WorkdayAttendanceAlertTone = 'late' | 'no_show'

export type WorkdayAttendanceJobGroup = {
  jobId: string
  jobTitle: string
  jobRef?: string | null
  items: DashboardWorkdayAttendanceAlertItem[]
}

const formatLateLabel = (minutes: number) => {
  const total = Math.max(0, Math.round(minutes))
  if (total < 60) return `${total} min late`
  const hours = Math.floor(total / 60)
  const remainder = total % 60
  if (remainder === 0) return `${hours}h late`
  return `${hours}h ${remainder}m late`
}

const formatWallClockDateTime = (value: string, locale: string) => {
  const match = value.trim().match(/^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})/)
  if (!match) return formatDateTimeForLocale(value, locale, value)
  const [, year, month, day, hour, minute] = match
  const wallClock = new Date(Date.UTC(Number(year), Number(month) - 1, Number(day), Number(hour), Number(minute)))
  return new Intl.DateTimeFormat(locale, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZone: 'UTC',
  }).format(wallClock)
}

const formatScheduleLabel = (
  item: DashboardWorkdayAttendanceAlertItem,
  locale: string,
  timeZone?: string,
  tenantTimeZone?: string | null,
) => {
  // With the tenant timezone: the stored instant in tenant time (not the worker-zone *_local value),
  // and the work date unshifted.
  if (tenantTimeZone) {
    if (item.planned_start) return formatDateTimeForLocale(item.planned_start, locale, formatDate(item.planned_start), tenantTimeZone)
    if (item.work_date) return formatDateOnly(item.work_date) ?? formatDate(item.work_date)
    return 'Schedule pending'
  }
  const local = item.planned_start_local?.trim()
  if (local) return formatWallClockDateTime(local, locale)
  if (item.planned_start) return formatDateTimeForLocale(item.planned_start, locale, formatDate(item.planned_start), timeZone)
  if (item.work_date) return formatDate(item.work_date)
  return 'Schedule pending'
}

export function groupWorkdayAttendanceAlertsByJob(
  items: DashboardWorkdayAttendanceAlertItem[],
): WorkdayAttendanceJobGroup[] {
  const groups = new Map<string, WorkdayAttendanceJobGroup>()

  items.forEach((item) => {
    const jobId = String(item.job_id ?? item.id)
    const existing = groups.get(jobId)
    if (existing) {
      existing.items.push(item)
      return
    }

    groups.set(jobId, {
      jobId,
      jobTitle: item.job_title?.trim() || `Job #${jobId}`,
      jobRef: item.job_ref,
      items: [item],
    })
  })

  return Array.from(groups.values())
}

const toneStyles: Record<WorkdayAttendanceAlertTone, {
  border: string
  background: string
  iconBackground: string
  iconColor: string
  title: string
  description: string
  badge: string
  groupBorder: string
  label: string
}> = {
  late: {
    border: 'border-amber-300',
    background: 'bg-amber-50/90 hover:bg-amber-50',
    iconBackground: 'bg-amber-100',
    iconColor: 'text-amber-700',
    title: 'text-amber-950',
    description: 'text-amber-900/80',
    badge: 'bg-amber-100 text-amber-800',
    groupBorder: 'border-amber-200',
    label: 'Late',
  },
  no_show: {
    border: 'border-rose-300',
    background: 'bg-rose-50/90 hover:bg-rose-50',
    iconBackground: 'bg-rose-100',
    iconColor: 'text-rose-700',
    title: 'text-rose-950',
    description: 'text-rose-900/80',
    badge: 'bg-rose-100 text-rose-800',
    groupBorder: 'border-rose-200',
    label: 'No show',
  },
}

export function GroupedAttendanceAlertList({
  items,
  tone,
  serviceInstanceId,
  returnPath,
}: {
  items: DashboardWorkdayAttendanceAlertItem[]
  tone: WorkdayAttendanceAlertTone
  serviceInstanceId?: string | null
  returnPath?: string | null
}) {
  const groups = useMemo(() => groupWorkdayAttendanceAlertsByJob(items), [items])
  const styles = toneStyles[tone]
  const { locale, timeZone } = useOrganizationDateTimeFormat(serviceInstanceId)
  // Dashboard-only component: tenant timezone resolved here; until it is known the existing output.
  const tenantTimeZone = useTenantTimeZone(serviceInstanceId)

  if (groups.length === 0) {
    return null
  }

  return (
    <div className="space-y-2">
      {groups.map((group) => (
        <div key={group.jobId} className={cn('rounded-lg border bg-white/70 p-2', styles.groupBorder)}>
          <div className="flex flex-wrap items-center gap-1.5 px-1 pb-1">
            <JobTitleLink
              jobId={group.jobId}
              title={group.jobTitle}
              jobRef={group.jobRef}
              serviceInstanceId={serviceInstanceId}
              returnPath={returnPath}
              className="truncate text-xs font-semibold text-foreground hover:text-primary"
            />
            {group.jobRef ? (
              <Badge variant="outline" className="px-1 py-0 text-[9px]">{group.jobRef}</Badge>
            ) : null}
            <span className={cn('rounded px-1 py-0 text-[9px] font-semibold', styles.badge)}>
              {group.items.length} {group.items.length === 1 ? 'worker' : 'workers'}
            </span>
          </div>
          <div className="space-y-1">
            {group.items.map((item) => (
              <Link
                key={String(item.id)}
                to={withReturnTo(withServiceInstanceId(`/workdays/${item.id}/edit`, serviceInstanceId), returnPath)}
                className="flex items-start justify-between gap-2 rounded-md px-2 py-1.5 transition-colors hover:bg-white"
              >
                <div className="min-w-0">
                  <p className="truncate text-[11px] font-medium text-foreground">
                    {item.worker_name || 'Unknown worker'}
                  </p>
                  <p className="mt-0.5 text-[10px] text-muted-foreground">
                    {formatScheduleLabel(item, locale, timeZone, tenantTimeZone)}
                    {tone === 'late' && Number(item.late_minutes ?? 0) > 0 ? ` · ${formatLateLabel(Number(item.late_minutes))}` : ''}
                  </p>
                </div>
                <span className={cn('shrink-0 rounded px-1 py-0 text-[9px] font-semibold', styles.badge)}>
                  {styles.label}
                </span>
              </Link>
            ))}
          </div>
        </div>
      ))}
    </div>
  )
}

function AttendanceAlertNotice({
  count,
  items,
  tone,
  title,
  serviceInstanceId,
  returnPath,
}: {
  count: number
  items: DashboardWorkdayAttendanceAlertItem[]
  tone: WorkdayAttendanceAlertTone
  title: string
  serviceInstanceId?: string | null
  returnPath?: string | null
}) {
  const [open, setOpen] = useState(false)
  const styles = toneStyles[tone]
  const labels = useWorkspaceLabels()
  const jobGroups = useMemo(() => groupWorkdayAttendanceAlertsByJob(items), [items])
  const workdaysHref = withReturnTo(withServiceInstanceId('/workdays', serviceInstanceId), returnPath)
  const Icon = tone === 'late' ? Clock : UserX

  if (count <= 0) {
    return null
  }

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <div className={cn('overflow-hidden rounded-xl border', styles.border)}>
        <CollapsibleTrigger asChild>
          <button
            type="button"
            className={cn(
              'flex w-full items-start gap-2 px-3 py-2 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/60',
              styles.background,
            )}
          >
            <div className={cn('mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-md', styles.iconBackground, styles.iconColor)}>
              <Icon className="h-3.5 w-3.5" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-1.5">
                <p className={cn('text-xs font-semibold', styles.title)}>{title}</p>
                <span className={cn('rounded-full px-1.5 py-0 text-[10px] font-semibold', styles.badge)}>
                  {jobGroups.length} job{jobGroups.length === 1 ? '' : 's'}
                </span>
              </div>
            </div>
            <ChevronDown
              className={cn(
                'mt-1 h-4 w-4 shrink-0 text-muted-foreground transition-transform',
                open ? 'rotate-180' : undefined,
              )}
            />
          </button>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className="space-y-2 border-t border-border/50 bg-background/80 px-3 py-2.5">
            <div className="flex items-center justify-between gap-2">
              <p className="text-[11px] text-muted-foreground">
                {count} {count === 1 ? labels.workDayEntryLower : labels.workDayEntryPluralLower} across {jobGroups.length} job{jobGroups.length === 1 ? '' : 's'}
              </p>
              <Link
                to={workdaysHref}
                className={cn('text-[11px] font-medium underline-offset-2 hover:underline', styles.title)}
              >
                Open {labels.workDayEntryPluralLower}
              </Link>
            </div>
            <GroupedAttendanceAlertList
              items={items}
              tone={tone}
              serviceInstanceId={serviceInstanceId}
              returnPath={returnPath}
            />
          </div>
        </CollapsibleContent>
      </div>
    </Collapsible>
  )
}

export function WorkDayAttendanceAlerts({
  lateCount = 0,
  noShowCount = 0,
  lateItems = [],
  noShowItems = [],
  isLoading = false,
  serviceInstanceId,
  returnPath,
}: WorkDayAttendanceAlertsProps) {
  const labels = useWorkspaceLabels()
  if (isLoading || (lateCount <= 0 && noShowCount <= 0)) {
    return null
  }

  return (
    <section className="space-y-2">
      {lateCount > 0 ? (
        <AttendanceAlertNotice
          count={lateCount}
          items={lateItems}
          tone="late"
          title={`${lateCount} ${lateCount === 1 ? labels.workDayEntryLower : labels.workDayEntryPluralLower} ${lateCount === 1 ? 'is' : 'are'} late`}
          serviceInstanceId={serviceInstanceId}
          returnPath={returnPath}
        />
      ) : null}

      {noShowCount > 0 ? (
        <AttendanceAlertNotice
          count={noShowCount}
          items={noShowItems}
          tone="no_show"
          title={`${noShowCount} ${labels.workDayEntryLower} no-show${noShowCount === 1 ? '' : 's'}`}
          serviceInstanceId={serviceInstanceId}
          returnPath={returnPath}
        />
      ) : null}
    </section>
  )
}
